/**
 * The gift homepage:
 *   · dm-home-film (sections/dm-home-film.liquid): the muted loop and its pause and sound controls.
 *     Under reduced motion it holds on the poster until someone presses play.
 *   · dm-home-gifts (sections/dm-home-gifts.liquid): the "Who's it for?" tiles swap the two grids
 *     the server already rendered. The stagger and the picked check are CSS, keyed on data-touched.
 *   · dm-home-proof (sections/dm-home-proof.liquid): each video on the wall plays muted while it is on
 *     screen and has its own pause. Under reduced motion or data saver they hold until pressed.
 *
 * The quiz button in dm-home-gifts carries data-dm-quiz-open. The quiz listens for it on document.
 */

const calm = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------ film */

class DmHomeFilm extends HTMLElement {
  #phone = window.matchMedia('(max-width: 749px)');
  #wantsPlay = !calm();
  #inView = false;
  #sound = false;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#playButton()?.addEventListener('click', this.#onPlay, { signal });
    this.#soundButton()?.addEventListener('click', this.#onSound, { signal });
    this.#phone.addEventListener('change', this.#sync, { signal });

    // It only runs while it can be seen, and picks up again on the way back.
    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.at(-1);
        this.#inView = Boolean(entry?.isIntersecting);
        this.#sync();
      },
      { threshold: 0.25 }
    );
    this.#observer.observe(this);
    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    for (const video of this.querySelectorAll('video')) video.pause();
  }

  #playButton() {
    return this.querySelector('[data-dm-film-play]');
  }

  #soundButton() {
    return this.querySelector('[data-dm-film-sound]');
  }

  /** The video on show at this width: a separate phone video only exists when one was set. */
  #current() {
    const phone = this.querySelector('.dm-home-film__video--phone');
    const video = phone && this.#phone.matches ? phone : this.querySelector('.dm-home-film__video--main');
    return video instanceof HTMLVideoElement ? video : null;
  }

  #onPlay = () => {
    this.#wantsPlay = !this.#wantsPlay;
    this.#sync();
  };

  #onSound = () => {
    this.#sound = !this.#sound;
    if (this.#sound) this.#wantsPlay = true;
    this.#sync();
  };

  #sync = () => {
    const current = this.#current();
    for (const video of this.querySelectorAll('video')) {
      if (video !== current) video.pause();
    }

    if (current) {
      current.muted = !this.#sound;
      if (this.#wantsPlay && this.#inView) {
        current.play().catch((error) => {
          // Low power mode or a blocked unmute: stay on the frame and offer play.
          if (error?.name !== 'NotAllowedError') return;
          this.#wantsPlay = false;
          this.#render();
        });
      } else {
        current.pause();
      }
    }

    this.#render();
  };

  #render() {
    const play = this.#playButton();
    play?.setAttribute('aria-label', this.#wantsPlay ? 'Pause the film' : 'Play the film');
    play?.toggleAttribute('data-on', this.#wantsPlay);

    const sound = this.#soundButton();
    sound?.setAttribute('aria-label', this.#sound ? 'Turn sound off' : 'Turn sound on');
    sound?.toggleAttribute('data-on', this.#sound);
  }
}

/* ------------------------------------------------------------------ who's it for */

class DmHomeGifts extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const tile = event.target.closest('[data-dm-gifts-pick]');
    if (!(tile instanceof HTMLElement)) return;

    const picked = tile.dataset.dmGiftsPick;
    this.toggleAttribute('data-touched', true);

    for (const option of this.querySelectorAll('[data-dm-gifts-pick]')) {
      option.setAttribute('aria-pressed', String(option === tile));
    }
    for (const grid of this.querySelectorAll('[data-dm-gifts-set]')) {
      if (grid instanceof HTMLElement) grid.hidden = grid.dataset.dmGiftsSet !== picked;
    }
    for (const link of this.querySelectorAll('[data-dm-gifts-link]')) {
      if (link instanceof HTMLElement) link.hidden = link.dataset.dmGiftsLink !== picked;
    }
  };
}

/* ------------------------------------------------------------------ proof wall */

class DmHomeProof extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#abort = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });

    const hold = calm() || Boolean(navigator.connection?.saveData);
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          entry.target.toggleAttribute('data-in-view', entry.isIntersecting);
          this.#sync(entry.target);
        }
      },
      { threshold: 0.5 }
    );
    for (const tile of this.querySelectorAll('[data-dm-proof-video]')) {
      tile.toggleAttribute('data-paused', hold);
      this.#render(tile);
      this.#observer.observe(tile);
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    for (const video of this.querySelectorAll('video')) video.pause();
  }

  /** @param {Event} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const tile = event.target.closest('[data-dm-proof-pause]')?.closest('[data-dm-proof-video]');
    if (!tile) return;
    tile.toggleAttribute('data-paused');
    this.#sync(tile);
  };

  /** @param {Element} tile */
  #sync(tile) {
    const video = tile.querySelector('video');
    if (!video) return;
    video.muted = true;
    if (tile.hasAttribute('data-in-view') && !tile.hasAttribute('data-paused')) {
      video.play().catch((error) => {
        // Low power mode: stay on the frame and offer play.
        if (error?.name !== 'NotAllowedError') return;
        tile.toggleAttribute('data-paused', true);
        this.#render(tile);
      });
    } else {
      video.pause();
    }
    this.#render(tile);
  }

  /** @param {Element} tile */
  #render(tile) {
    const button = tile.querySelector('[data-dm-proof-pause]');
    const playing = !tile.hasAttribute('data-paused');
    button?.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    button?.toggleAttribute('data-on', playing);
  }
}

if (!customElements.get('dm-home-film')) customElements.define('dm-home-film', DmHomeFilm);
if (!customElements.get('dm-home-gifts')) customElements.define('dm-home-gifts', DmHomeGifts);
if (!customElements.get('dm-home-proof')) customElements.define('dm-home-proof', DmHomeProof);
